import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAdminMaterials } from "@/api/queries/admin/catalog";
import type { MaterialPropertyValueRow } from "@/components/admin/MaterialPropertyValuesPanel";
import { useProcessTypeLabel, useShopProcessFilter } from "@/lib/useProcessTypeLabel";

export interface PickerMaterial {
  id: number;
  code: string;
  display_name: string;
  /** The material-property registry rows, verbatim from the admin catalog.
   *  Each row NAMES its own variant axis in ``variant_signature``, which is
   *  how ``DimensionPicker`` finds the values offered on a process's declared
   *  axis — for ANY axis, without naming one. */
  property_values?: MaterialPropertyValueRow[];
  process_types: string[];
  /** Slug kategorie materiálu — zdroj os a výchozí skladové jednotky pro
   *  ``useMaterialDimensions``. Volitelný: starší stavitelé ho nenesou a
   *  hook si ho dočte z registry řádku ``material_category``. */
  material_category?: string | null;
  swatch_hex: string | null;
  swatch_image_url: string | null;
  tracking_mode: string;
  inventory_managed: boolean;
}

interface Props {
  value: number | null;
  onChange: (material: PickerMaterial | null) => void;
  processType?: string | null;
  disabled?: boolean;
  /**
   * If true, materials with ``inventory_managed=false`` are visible but
   * tagged as such — the parent surface decides whether to flip the
   * flag or block the action.
   */
  showInventoryFlag?: boolean;
  placeholder?: string;
}

export function MaterialSwatch({
  swatchHex,
  swatchImageUrl,
  alt,
}: {
  swatchHex: string | null;
  swatchImageUrl: string | null;
  alt: string;
}): JSX.Element {
  if (swatchImageUrl) {
    return (
      <img
        src={swatchImageUrl}
        alt={alt}
        className="h-5 w-5 rounded border border-border object-cover"
      />
    );
  }
  if (swatchHex) {
    return (
      <span
        className="inline-block h-5 w-5 rounded border border-border"
        style={{ backgroundColor: swatchHex }}
        aria-label={alt}
      />
    );
  }
  return (
    <span
      className="inline-block h-5 w-5 rounded border border-border bg-gradient-to-br from-surface-2 to-surface-3"
      aria-label={alt}
    />
  );
}

export function MaterialPicker({
  value,
  onChange,
  processType,
  disabled = false,
  showInventoryFlag = true,
  placeholder,
}: Props): JSX.Element {
  const { t } = useTranslation("admin");
  // KAN-2114 — procesy obchodu s lidským názvem, ne ``LASER_SHEET / PLASMA_SHEET``.
  const shopProcesses = useShopProcessFilter();
  const processLabel = useProcessTypeLabel();
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const { data, isLoading } = useAdminMaterials(processType ?? undefined);
  const allMaterials = useMemo<PickerMaterial[]>(
    () =>
      (data?.items ?? []).map((m) => ({
        id: m.id,
        code: m.code,
        // ``||``, ne ``??``: ``create_material`` zakládá překladový řádek i
        // pro nevyplněný jazyk, takže ``display_name_cs`` chodí jako ``""`` —
        // ``??`` by prázdný řetězec ponechalo a řádek by se vykreslil bez textu.
        display_name: m.display_name_cs || m.display_name_en || m.code,
        property_values: m.property_values,
        process_types: m.process_types ?? [],
        material_category: m.material_category ?? null,
        swatch_hex: m.swatch_hex ?? null,
        swatch_image_url: m.swatch_image_url ?? null,
        tracking_mode: m.tracking_mode ?? "none",
        inventory_managed: m.inventory_managed ?? false,
      })),
    [data],
  );

  const selected = useMemo(
    () => allMaterials.find((m) => m.id === value) ?? null,
    [allMaterials, value],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return allMaterials;
    return allMaterials.filter(
      (m) =>
        m.code.toLowerCase().includes(q) ||
        m.display_name.toLowerCase().includes(q),
    );
  }, [allMaterials, search]);

  useEffect(() => {
    function onDocClick(e: MouseEvent): void {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const pick = (m: PickerMaterial): void => {
    onChange(m);
    setSearch("");
    setOpen(false);
  };

  return (
    <div ref={containerRef} className="relative" data-testid="material-picker">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-2 rounded border border-border bg-bg px-2 py-1.5 text-left text-sm disabled:opacity-50"
        data-testid="material-picker-toggle"
      >
        {selected ? (
          <span className="flex items-center gap-2 truncate">
            <MaterialSwatch
              swatchHex={selected.swatch_hex}
              swatchImageUrl={selected.swatch_image_url}
              alt={selected.code}
            />
            <span className="truncate font-medium">{selected.display_name}</span>
            {showInventoryFlag && !selected.inventory_managed && (
              <span className="rounded bg-warn-bg px-1.5 py-0.5 text-[10px] uppercase text-warn">
                {t("stock.picker.inventoryOff", {
                  defaultValue: "sklad vypnut",
                })}
              </span>
            )}
          </span>
        ) : (
          <span className="text-ink-3">
            {placeholder ??
              t("stock.picker.placeholder", {
                defaultValue: "Vyberte materiál…",
              })}
          </span>
        )}
        <span className="text-ink-3">▾</span>
      </button>

      {open && !disabled && (
        <div
          className="absolute left-0 right-0 z-30 mt-1 max-h-80 overflow-auto rounded border border-border bg-bg shadow-lg"
          role="listbox"
        >
          <div className="sticky top-0 border-b border-border bg-bg p-2">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoFocus
              placeholder={t("stock.picker.search", {
                defaultValue: "Hledat podle názvu…",
              })}
              className="w-full rounded border border-border bg-bg px-2 py-1 text-sm"
              data-testid="material-picker-search"
            />
          </div>
          {isLoading && (
            <p className="px-3 py-4 text-sm text-ink-3">
              {t("stock.picker.loading", { defaultValue: "Načítám…" })}
            </p>
          )}
          {!isLoading && filtered.length === 0 && (
            <p className="px-3 py-4 text-sm text-ink-3">
              {t("stock.picker.empty", {
                defaultValue: "Žádný materiál neodpovídá hledání.",
              })}
            </p>
          )}
          <ul>
            {filtered.map((m) => (
              <li key={m.id}>
                <button
                  type="button"
                  onClick={() => pick(m)}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-surface-2"
                  data-testid={`material-picker-option-${m.id}`}
                >
                  <MaterialSwatch
                    swatchHex={m.swatch_hex}
                    swatchImageUrl={m.swatch_image_url}
                    alt={m.code}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-medium text-ink">
                      {m.display_name}
                    </span>
                    {/* Sub-line used to lead with the material code; that is an
                        internal join key, so only the variant count remains. */}
                    {(m.property_values ?? []).some(
                      (v) => Object.keys(v.variant_signature ?? {}).length > 0,
                    ) && (
                      <span className="block truncate text-xs text-ink-3">
                        {
                          (m.property_values ?? []).filter(
                            (v) =>
                              v.is_active &&
                              Object.keys(v.variant_signature ?? {}).length > 0,
                          ).length
                        }{" "}
                        {t("stock.picker.variantsShort", {
                          defaultValue: "variant",
                        })}
                      </span>
                    )}
                  </span>
                  <span className="flex flex-wrap items-center gap-1">
                    {shopProcesses(m.process_types).slice(0, 3).map((pt) => (
                      <span
                        key={pt}
                        className="rounded bg-surface-2 px-1.5 py-0.5 text-[10px] text-ink-2"
                      >
                        {processLabel(pt)}
                      </span>
                    ))}
                    {showInventoryFlag && !m.inventory_managed && (
                      <span className="rounded bg-warn-bg px-1.5 py-0.5 text-[10px] uppercase text-warn">
                        {t("stock.picker.inventoryOff", {
                          defaultValue: "sklad vypnut",
                        })}
                      </span>
                    )}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
